import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
}

export function Modal({ open, onClose, title, children, footer }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    ref.current?.focus()
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/50" role="dialog">
      <div className="absolute inset-0" onClick={onClose} />
      <div ref={ref} tabIndex={-1}
        className="relative z-10 bg-paper rounded-card border border-ink-border shadow-elevated p-6 w-full max-w-lg animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-serif text-xl font-bold text-ink">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-ink-muted hover:text-brick hover:bg-paper-warm transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="divider-gold mb-5" />
        {/* Body */}
        <div>{children}</div>
        {/* Footer */}
        {footer && (
          <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-ink-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
